/**
 * useCreatorAnalytics - Creator Portal Data Hooks
 * 
 * Fetches aggregated training metrics for creators.
 * Creators only see anonymized data from training_artifact_global_metrics.
 * 
 * Part of Training Intelligence System (Phase 5+6)
 */

import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface GlobalMetric {
  id: string;
  artifact_id: string;
  sample_shops: number;
  sample_technicians: number;
  total_sessions: number;
  verified_completions: number;
  likely_completions: number;
  suspicious_completions: number;
  avg_confidence_delta: number | null;
  avg_efficiency_delta_percent: number | null;
  avg_comeback_reduction_percent: number | null;
  job_category_impact_matrix: Record<string, number>;
  ymm_impact_matrix: Record<string, number>;
  last_updated_at: string;
  // Phase 5 columns
  total_opens: number | null; // R4 CORE-S: null below the privacy floor
  unique_technician_opens: number | null;
  behavioral_notes_rate: number;
  behavioral_progress_rate: number;
  behavioral_additional_training_rate: number;
  avg_pensieve_score: number;
  pensieve_sample_count: number;
  primary_domain: string | null;
}

export interface CreatorArtifact {
  id: string;
  artifact_id: string;
  relationship: string;
  artifact: {
    id: string;
    title: string;
    provider_name: string;
    artifact_type: string;
    skill_depth: string;
  };
  metrics?: GlobalMetric | null;
}

/**
 * Fetch creator's account info
 */
export function useCreatorAccount() {
  return useQuery({
    queryKey: ['creator-account'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;

      const { data, error } = await supabase
        .from('training_creator_accounts')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) throw error;
      return data;
    },
  });
}

/**
 * Fetch all artifacts linked to the creator with their metrics
 * @param overrideCreatorId - Optional: fetch for a specific creator (admin only)
 */
export function useCreatorArtifacts(overrideCreatorId?: string) {
  return useQuery({
    queryKey: ['creator-artifacts', overrideCreatorId || 'self'],
    queryFn: async () => {
      let creatorId = overrideCreatorId;
      
      if (!creatorId) {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return [];

        // Get own creator account
        const { data: account } = await supabase
          .from('training_creator_accounts')
          .select('id')
          .eq('user_id', user.id)
          .maybeSingle();

        if (!account) return [];
        creatorId = account.id;
      }

      // Get linked artifacts with their metrics
      const { data: artifacts, error } = await supabase
        .from('training_creator_artifacts')
        .select(`
          id,
          artifact_id,
          relationship,
          training_artifacts (
            id,
            title,
            provider_name,
            artifact_type,
            skill_depth
          )
        `)
        .eq('creator_id', creatorId);

      if (error) throw error;

      // Fetch metrics for all artifacts
      const artifactIds = artifacts?.map(a => a.artifact_id) || [];
      
      if (artifactIds.length === 0) return [];

      const { data: metrics } = await supabase
        .from('training_artifact_global_metrics')
        .select('*')
        .in('artifact_id', artifactIds);

      // Merge metrics with artifacts
      const metricsMap = new Map(
        (metrics || []).map(m => [m.artifact_id, m])
      );

      return (artifacts || []).map(a => ({
        id: a.id,
        artifact_id: a.artifact_id,
        relationship: a.relationship,
        artifact: a.training_artifacts,
        metrics: metricsMap.get(a.artifact_id) || null,
      })) as CreatorArtifact[];
    },
  });
}

/**
 * Fetch metrics for a single artifact
 */
export function useArtifactMetrics(artifactId: string | undefined) {
  return useQuery({
    queryKey: ['artifact-metrics', artifactId],
    queryFn: async () => {
      if (!artifactId) return null;

      const { data, error } = await supabase
        .from('training_artifact_global_metrics')
        .select('*')
        .eq('artifact_id', artifactId)
        .maybeSingle();

      if (error) throw error;
      return data as GlobalMetric | null;
    },
    enabled: !!artifactId,
  });
}

/**
 * Calculate summary stats across all creator's artifacts
 * @param overrideCreatorId - Optional: calculate for a specific creator (admin only)
 */
export function useCreatorSummaryStats(overrideCreatorId?: string) {
  const { data: artifacts } = useCreatorArtifacts(overrideCreatorId);

  if (!artifacts || artifacts.length === 0) {
    return {
      totalArtifacts: 0,
      totalSessions: 0,
      totalTechnicians: 0,
      totalShops: 0,
      avgConfidenceDelta: null,
      avgEfficiencyDelta: null,
      verifiedCompletions: 0,
      likelyCompletions: 0,
      totalOpens: 0,
      avgBehavioralRate: null,
    };
  }

  const metricsWithData = artifacts
    .filter(a => a.metrics && a.metrics.sample_shops >= 3 && a.metrics.sample_technicians >= 5);

  const totalSessions = metricsWithData.reduce((sum, a) => sum + (a.metrics?.total_sessions || 0), 0);
  
  // Get unique counts (approximate - actual dedup would need backend)
  const totalTechnicians = metricsWithData.reduce((sum, a) => sum + (a.metrics?.sample_technicians || 0), 0);
  const totalShops = metricsWithData.reduce((sum, a) => sum + (a.metrics?.sample_shops || 0), 0);

  // Weighted averages
  let avgConfidenceDelta: number | null = null;
  let avgEfficiencyDelta: number | null = null;

  const confidenceDeltas = metricsWithData
    .filter(a => a.metrics?.avg_confidence_delta !== null)
    .map(a => ({
      value: a.metrics!.avg_confidence_delta!,
      weight: a.metrics!.total_sessions,
    }));

  if (confidenceDeltas.length > 0) {
    const totalWeight = confidenceDeltas.reduce((sum, d) => sum + d.weight, 0);
    avgConfidenceDelta = confidenceDeltas.reduce((sum, d) => sum + d.value * d.weight, 0) / totalWeight;
  }

  const efficiencyDeltas = metricsWithData
    .filter(a => a.metrics?.avg_efficiency_delta_percent !== null)
    .map(a => ({
      value: a.metrics!.avg_efficiency_delta_percent!,
      weight: a.metrics!.total_sessions,
    }));

  if (efficiencyDeltas.length > 0) {
    const totalWeight = efficiencyDeltas.reduce((sum, d) => sum + d.weight, 0);
    avgEfficiencyDelta = efficiencyDeltas.reduce((sum, d) => sum + d.value * d.weight, 0) / totalWeight;
  }

  const verifiedCompletions = metricsWithData.reduce((sum, a) => sum + (a.metrics?.verified_completions || 0), 0);
  const likelyCompletions = metricsWithData.reduce((sum, a) => sum + (a.metrics?.likely_completions || 0), 0);

  // Phase 5: Total opens
  const totalOpens = metricsWithData.reduce((sum, a) => sum + (a.metrics?.total_opens || 0), 0);

  // Phase 5: Weighted average behavioral rate (weighted by total_sessions)
  let avgBehavioralRate: number | null = null;
  const behavioralData = metricsWithData
    .filter(a => a.metrics && (a.metrics.behavioral_notes_rate > 0 || a.metrics.behavioral_progress_rate > 0))
    .map(a => ({
      rate: ((a.metrics!.behavioral_notes_rate || 0) + (a.metrics!.behavioral_progress_rate || 0)) / 2,
      weight: a.metrics!.total_sessions,
    }));

  if (behavioralData.length > 0) {
    const totalWeight = behavioralData.reduce((sum, d) => sum + d.weight, 0);
    avgBehavioralRate = behavioralData.reduce((sum, d) => sum + d.rate * d.weight, 0) / totalWeight;
  }

  return {
    totalArtifacts: artifacts.length,
    totalSessions,
    totalTechnicians,
    totalShops,
    avgConfidenceDelta,
    avgEfficiencyDelta,
    verifiedCompletions,
    likelyCompletions,
    totalOpens,
    avgBehavioralRate,
  };
}

// =============================================
// Phase 5: Domain-level aggregation
// =============================================

export interface DomainStats {
  domain: string;
  artifactCount: number;
  totalSessions: number;
  avgPensieveScore: number;
  avgBehavioralRate: number;
}

/**
 * Aggregate creator artifact metrics by primary_domain.
 * Client-side grouping from already-fetched artifacts — zero extra queries.
 */
export function useCreatorDomainStats(overrideCreatorId?: string): DomainStats[] {
  const { data: artifacts } = useCreatorArtifacts(overrideCreatorId);

  return useMemo(() => {
    if (!artifacts || artifacts.length === 0) return [];

    const domainMap = new Map<string, {
      artifactCount: number;
      totalSessions: number;
      pensieveScoreSum: number;
      pensieveSampleSum: number;
      behavioralRateSum: number;
      behavioralWeightSum: number;
    }>();

    for (const a of artifacts) {
      if (!a.metrics || a.metrics.sample_shops < 3 || a.metrics.sample_technicians < 5) continue;

      const domain = a.metrics.primary_domain || 'Uncategorized';
      const entry = domainMap.get(domain) || {
        artifactCount: 0,
        totalSessions: 0,
        pensieveScoreSum: 0,
        pensieveSampleSum: 0,
        behavioralRateSum: 0,
        behavioralWeightSum: 0,
      };

      entry.artifactCount += 1;
      entry.totalSessions += a.metrics.total_sessions || 0;

      // Weighted pensieve score
      const pSamples = a.metrics.pensieve_sample_count || 0;
      entry.pensieveScoreSum += (a.metrics.avg_pensieve_score || 0) * pSamples;
      entry.pensieveSampleSum += pSamples;

      // Weighted behavioral rate
      const sessions = a.metrics.total_sessions || 0;
      const avgRate = ((a.metrics.behavioral_notes_rate || 0) + (a.metrics.behavioral_progress_rate || 0)) / 2;
      entry.behavioralRateSum += avgRate * sessions;
      entry.behavioralWeightSum += sessions;

      domainMap.set(domain, entry);
    }

    const result: DomainStats[] = [];
    for (const [domain, entry] of domainMap) {
      result.push({
        domain,
        artifactCount: entry.artifactCount,
        totalSessions: entry.totalSessions,
        avgPensieveScore: entry.pensieveSampleSum > 0
          ? entry.pensieveScoreSum / entry.pensieveSampleSum
          : 0,
        avgBehavioralRate: entry.behavioralWeightSum > 0
          ? entry.behavioralRateSum / entry.behavioralWeightSum
          : 0,
      });
    }

    // Sort by sessions desc
    result.sort((a, b) => b.totalSessions - a.totalSessions);
    return result;
  }, [artifacts]);
}
